/**
 * /venues/:venueSlug/players/:id — a player's public record at this venue.
 *
 * Public, and the only page that renders a hidden profile. The endpoint answers a
 * hidden player with their display name and nothing else — no avatar, no figures,
 * no history — so the two states here are genuinely different pages rather than
 * one page with parts blanked out. Reading anything but the name off a hidden
 * response would be reading fields that are not there.
 *
 * A 404 means the account does not exist, is closed, or has never played at this
 * venue. It does NOT mean hidden — that is a 200, because the name is already on
 * every bracket and standings table the venue publishes, and a 404 would make a
 * hidden player look deleted.
 *
 * Below the heading this is the same component My Stats renders, with no season
 * selector: a spectator gets the current season, and a closed season's table is
 * published through the hall of fame rather than here.
 */

import { Link, useParams } from 'react-router-dom';
import { apiGet } from '../lib/api.js';
import { useVenuePage } from '../lib/useVenuePage.js';
// memberSince is users.created_at, a CURRENT_TIMESTAMP rather than a date, so it
// needs formatStampDate — formatDateShort splits on '-' and hands back the raw
// stamp when the day part is '12 17:48:07'.
import { formatStampDate } from '../lib/format.js';
import PlayerStatsBody from '../components/stats/PlayerStatsBody.jsx';

/** The same constant Profile.jsx uses. profile_pic_url is NULL, never this path. */
const DEFAULT_AVATAR = '/default-avatar.jpg';

export default function PublicProfile() {
  const { venueSlug, id } = useParams();

  const state = useVenuePage(
    venueSlug,
    (venue) => apiGet(`/api/venues/${venue.id}/players/${id}/public-profile`),
    [id]
  );

  if (state.status === 'loading') {
    return <div className="hd-empty">Loading…</div>;
  }

  if (state.status === 'error') {
    return (
      <div className="hd-stack hd-stats-page">
        <h1 className="hd-page-title">
          {state.venue ? 'Player not found' : 'Venue not found'}
        </h1>
        <p className="hd-muted">
          {state.venue
            ? <>No player here with that id.</>
            : <>No venue at <code>/{venueSlug}</code>.</>}
          {' '}
          <Link to={state.venue ? `/venues/${venueSlug}` : '/'}>
            {state.venue ? 'Back to the venue' : 'Back to the home page'}
          </Link>
        </p>
      </div>
    );
  }

  const { venue, data } = state;

  // Everything else on the response is absent in this state, so it returns before
  // anything can read it.
  if (data.hidden) {
    return (
      <div className="hd-stack hd-stats-page">
        <div className="hd-stats-head">
          <h1 className="hd-page-title">{data.displayName}</h1>
          <p className="hd-muted">This player has set their profile to hidden.</p>
        </div>
      </div>
    );
  }

  const { player, season, allTime, tournaments } = data;

  return (
    <div className="hd-stack hd-stats-page">
      <div className="hd-stats-head">
        <img
          className="hd-avatar hd-avatar-centred"
          src={player.profilePicUrl ?? DEFAULT_AVATAR}
          alt=""
        />
        <h1 className="hd-page-title">{player.displayName}</h1>
        <p className="hd-meta">
          <Link to={`/venues/${venue.slug}`}>{venue.name}</Link>
          {formatStampDate(player.memberSince) && (
            <> · member since {formatStampDate(player.memberSince)}</>
          )}
        </p>
      </div>

      <PlayerStatsBody
        venueSlug={venue.slug}
        season={season}
        allTime={allTime}
        tournaments={tournaments}
      />
    </div>
  );
}
